import { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import {
  Rocket, Clock, CheckCircle, XCircle, Loader2,
  Zap, Plus, Trash2, AlertTriangle
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import { toast } from 'sonner';

const DURATION_OPTIONS = [
  { value: '24h', label: '24 Hours' },
  { value: '3d', label: '3 Days' },
  { value: '7d', label: '7 Days' },
];

const TARGET_TYPES = [
  { value: 'track', label: 'Song' },
  { value: 'video', label: 'Video' },
  { value: 'post', label: 'Post' },
  { value: 'profile', label: 'My Profile' },
  { value: 'clip', label: 'Clip' },
  { value: 'podcast', label: 'Podcast' },
];

const STATUS_COLORS = {
  active: 'bg-green-500/20 text-green-400 border-green-500/30',
  pending: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  expired: 'bg-muted/40 text-muted-foreground border-border/30',
  cancelled: 'bg-red-500/20 text-red-400 border-red-500/30',
  denied: 'bg-red-500/20 text-red-400 border-red-500/30',
};

export function ContentBoostManager() {
  const { token, user } = useAuth();
  const [boosts, setBoosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ target_type: '', target_id: '', duration: '24h', note: '' });

  useEffect(() => {
    if (token) fetchBoosts();
  }, [token]);

  const fetchBoosts = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API}/boost/my-boosts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setBoosts(data.boosts || []);
      }
    } catch {
      toast.error('Failed to load boosts');
    } finally {
      setLoading(false);
    }
  };

  const createBoost = async () => {
    if (!form.target_type) { toast.error('Select content type'); return; }
    if (form.target_type !== 'profile' && !form.target_id) { toast.error('Enter content ID'); return; }

    try {
      setCreating(true);
      const body = {
        target_type: form.target_type,
        target_id: form.target_type === 'profile' ? user.user_id : form.target_id,
        duration: form.duration,
        note: form.note || null,
      };
      const res = await fetch(`${API}/boost/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        toast.success('Boost activated!');
        setShowForm(false);
        setForm({ target_type: '', target_id: '', duration: '24h', note: '' });
        fetchBoosts();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create boost');
      }
    } catch {
      toast.error('Failed to create boost');
    } finally {
      setCreating(false);
    }
  };

  const cancelBoost = async (boostId) => {
    try {
      const res = await fetch(`${API}/boost/${boostId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success('Boost cancelled');
        fetchBoosts();
      }
    } catch {
      toast.error('Failed to cancel boost');
    }
  };

  const activeBoosts = boosts.filter(b => b.status === 'active');
  const pastBoosts = boosts.filter(b => b.status !== 'active');

  const getTimeRemaining = (expiresAt) => {
    const diff = new Date(expiresAt) - new Date();
    if (diff <= 0) return 'Expired';
    const hours = Math.floor(diff / 3600000);
    const mins = Math.floor((diff % 3600000) / 60000);
    if (hours > 24) return `${Math.floor(hours / 24)}d ${hours % 24}h`;
    return `${hours}h ${mins}m`;
  };

  return (
    <div className="space-y-4" data-testid="content-boost-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Rocket className="w-5 h-5 text-secondary" />
          <h3 className="font-heading font-bold text-lg">Content Boosts</h3>
        </div>
        <Button
          size="sm"
          onClick={() => setShowForm(!showForm)}
          className="bg-secondary hover:bg-secondary/80 text-black"
          data-testid="create-boost-btn"
        >
          <Plus className="w-4 h-4 mr-1" />
          New Boost
        </Button>
      </div>

      <p className="text-xs text-muted-foreground">
        Boost your content for priority placement in feeds, discovery, and search results.
      </p>

      {/* Create Boost Form */}
      {showForm && (
        <Card className="bg-card border-secondary/20 p-5" data-testid="boost-create-form">
          <h4 className="font-bold text-sm mb-3 flex items-center gap-2">
            <Zap className="w-4 h-4 text-secondary" />
            Create New Boost
          </h4>
          <div className="grid sm:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="text-[11px] text-muted-foreground uppercase mb-1 block">Content Type</label>
              <Select value={form.target_type} onValueChange={(v) => setForm({ ...form, target_type: v, target_id: v === 'profile' ? '' : form.target_id })}>
                <SelectTrigger data-testid="boost-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>
                  {TARGET_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] text-muted-foreground uppercase mb-1 block">Duration</label>
              <Select value={form.duration} onValueChange={(v) => setForm({ ...form, duration: v })}>
                <SelectTrigger data-testid="boost-duration-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DURATION_OPTIONS.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {form.target_type && form.target_type !== 'profile' && (
            <div className="mb-3">
              <label className="text-[11px] text-muted-foreground uppercase mb-1 block">Content ID</label>
              <Input
                value={form.target_id}
                onChange={e => setForm({ ...form, target_id: e.target.value })}
                placeholder="Enter the content ID to boost"
                data-testid="boost-target-id-input"
              />
            </div>
          )}
          <div className="mb-4">
            <label className="text-[11px] text-muted-foreground uppercase mb-1 block">Note (optional)</label>
            <Input
              value={form.note}
              onChange={e => setForm({ ...form, note: e.target.value })}
              placeholder="Why are you boosting this?"
              data-testid="boost-note-input"
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={createBoost} disabled={creating} className="bg-secondary hover:bg-secondary/80 text-black" data-testid="submit-boost-btn">
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Rocket className="w-4 h-4 mr-1" />}
              Activate Boost
            </Button>
            <Button variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
          </div>
        </Card>
      )}

      {/* Active Boosts */}
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
      ) : (
        <>
          {activeBoosts.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-xs text-muted-foreground uppercase tracking-wide font-bold">Active Boosts</h4>
              {activeBoosts.map(boost => (
                <Card key={boost.boost_id} className="bg-green-500/5 border-green-500/20 p-4 flex items-center gap-4" data-testid={`boost-${boost.boost_id}`}>
                  <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center flex-shrink-0">
                    <Rocket className="w-5 h-5 text-green-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <p className="font-medium text-sm capitalize">{boost.target_type} Boost</p>
                      <Badge className={STATUS_COLORS.active}>{boost.status}</Badge>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {getTimeRemaining(boost.expires_at)} left</span>
                      <span>{boost.duration}</span>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300" onClick={() => cancelBoost(boost.boost_id)} data-testid={`cancel-boost-${boost.boost_id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </Card>
              ))}
            </div>
          )}

          {activeBoosts.length === 0 && !showForm && (
            <Card className="bg-card border-border p-6 text-center" data-testid="no-active-boosts">
              <Rocket className="w-8 h-8 mx-auto mb-2 text-muted-foreground/80" />
              <p className="text-sm text-muted-foreground">No active boosts</p>
              <p className="text-[10px] text-muted-foreground/80 mt-1">Boost your content to get more visibility</p>
            </Card>
          )}

          {pastBoosts.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-xs text-muted-foreground uppercase tracking-wide font-bold">Past Boosts</h4>
              {pastBoosts.slice(0, 5).map(boost => (
                <Card key={boost.boost_id} className="bg-card border-border p-3 flex items-center gap-3 opacity-60" data-testid={`past-boost-${boost.boost_id}`}>
                  <div className="w-8 h-8 rounded bg-muted flex items-center justify-center flex-shrink-0">
                    <Rocket className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium capitalize">{boost.target_type} Boost</p>
                    <p className="text-[10px] text-muted-foreground">{boost.duration} - {new Date(boost.created_at).toLocaleDateString()}</p>
                  </div>
                  <Badge className={STATUS_COLORS[boost.status] || STATUS_COLORS.expired}>{boost.status}</Badge>
                </Card>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
